import React from 'react';
import {createPortal} from 'react-dom';
import {useFloating, offset, flip, shift, autoUpdate} from '@floating-ui/react';
import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/react';
import {ChevronDownIcon} from '@heroicons/react/20/solid';
import {classNames} from 'utils';

type ItemType = {
  id: string;
  label: string;
  onClick: () => void;
};

interface IDropdownSingleProps {
  items: ItemType[];
  selected: ItemType;
}

const DropdownSingle = ({items, selected}: IDropdownSingleProps) => {
  const {x, y, refs, strategy} = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });
  return (
    <Menu as="div" className="relative inline-block">
      <MenuButton
        ref={refs.setReference}
        className="inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-white dark:bg-slate-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-xs inset-ring-1 inset-ring-gray-300 dark:inset-ring-slate-400 hover:bg-gray-50 dark:hover:bg-slate-600 cursor-pointer">
        {selected.label}
        <ChevronDownIcon aria-hidden="true" className="-mr-1 size-5 text-gray-400" />
      </MenuButton>
      {createPortal(
        <MenuItems
          transition
          ref={refs.setFloating}
          className="absolute right-0 z-10 mt-2 w-56 border border-slate-300 dark:border-slate-400 origin-top-right rounded-md bg-white dark:bg-slate-800 shadow-lg outline-1 outline-black/5 dark:outline-slate-400 transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            zIndex: 50,
          }}>
          <div className="py-1">
            {items.map(item => {
              return (
                <MenuItem key={item.id}>
                  <button
                    type="button"
                    className={classNames(
                      'block w-full px-4 py-2 text-sm text-gray-700 dark:text-white text-left cursor-pointer',
                      'data-focus:bg-gray-100 data-focus:outline-hidden dark:data-focus:bg-slate-700',
                      item.id === selected.id && 'bg-gray-100 dark:bg-slate-700',
                    )}
                    onClick={item.onClick}>
                    {item.label}
                  </button>
                </MenuItem>
              );
            })}
          </div>
        </MenuItems>,
        document.body,
      )}
    </Menu>
  );
};

export default React.memo(DropdownSingle);
